/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Payload from './Payload';

export default class Transport {
  public static ingestURL = 'https://telemetry-dev.adobe.io/ingest';
  public static flushPeriod = 1000;
  public static flushTimeoutId?: any;
  public static batchMode = true;
  public static lastBatch?: number;
  public static apiKey = 'metrics-sdk-js';
  public static maxPayloadSize = 500000;
  public static transmit = true;

  /**
   * Initialize the Transport layer. Currently this just initializes
   * the base url. The reason for an explicit init() is to definitively
   * control ordering and exception handling during startup.
   *
   * @param {object} config A congfiguration object for the desired state.
   */
  public static init(config?: {
    apiKey?: string;
  }): void {
    Transport.apiKey = config && config.apiKey ? config.apiKey : Transport.apiKey;
    const runtime = (global as any).adobeMetrics.getRuntime();
    const transport = runtime.options.transport;
    switch (runtime.environment) {
      case 'stage':
        Transport.ingestURL = `https://telemetry-stage.adobe.io`;
        break;
      case 'prod':
        Transport.ingestURL = 'https://telemetry.adobe.io';
        break;
      default:
        Transport.ingestURL = 'https://telemetry-dev.adobe.io';
    }
    Transport.ingestURL += '/ingest';
    Transport.flushPeriod = transport.flushPeriod;
    Transport.batchMode = transport.batch;
    Transport.transmit = transport.transmit;
    Transport.maxPayloadSize = transport.maxPayloadSize;
  }

  /**
   * HTTP POST the current payload to the metrics gateway for ingestion
   * into Azure Data Explorer. The object fields need to correspond to the
   * mapped table schema in ADE.
   *
   * @private
   * @returns {Promise<Response>} A promise for the response.
   */
  public static post(): Promise<Response> {
    return (global as any)._originalFetch(Transport.ingestURL + '/?api_key=' + Transport.apiKey, {
      body: Payload.toJSONString(),
      headers: {
        'Content-Type': 'text/plain;type=entry;charset=utf-8'
      },
      method: 'POST'
    }); // we do not process the response from a REST POST to metrics-gateway
  }

  /**
   * Send a payload to the metrics gateway for ingestion into Azure Data
   * explorer. The object fields need to correspond to the mapped table schema
   * in ADE.
   *
   * @private
   * @param {string} flush A string indicating the reason the payload should be
   * immediately flushed.
   * @returns {object} Return noting or flush non-empty payload.
   */
  public static send(flush: string | undefined = undefined): void {
    if (!Transport.transmit) {
      return;
    }

    // make note of the reason to flush
    if (flush || !Transport.batchMode && (flush = 'immediate') ||
      Payload.size > Transport.maxPayloadSize && (flush = 'maxSize')) {
      return Transport.flush(flush);
    }

    const now = Date.now();
    if (!Transport.lastBatch || now - Transport.lastBatch >= Transport.flushPeriod) {
      Transport.lastBatch = now;
      Transport.flushTimeoutId = setTimeout((): void => {return Transport.flush('timer');}, Transport.flushPeriod);
    }
  }

  /*
   * Flush non-empty payloads immediately.
   */
  public static flush(reason: string): void {
    // re-init in case runtime values changed
    // flushPeriod would be picked up for the next loop
    Transport.init();
    clearTimeout(Transport.flushTimeoutId);
    delete Transport.flushTimeoutId;

    if (!Payload.isEmpty()) {
      Payload.appendPayloadCount(reason);
      Transport.post();
      Transport.lastBatch = undefined;
      Payload.next();
    }
  }
}
